import React from 'react';
import { getInitials } from './shared/initials';

// ============================================================================
// DEFAULT AVATARS
// ============================================================================

export const DEFAULT_AVATARS = [
  {
    id: 'avatar-1',
    initials: 'A',
    bgColor: 'bg-blue-500',
    textColor: 'text-white',
  },
  {
    id: 'avatar-2',
    initials: 'B',
    bgColor: 'bg-green-500',
    textColor: 'text-white',
  },
  {
    id: 'avatar-3',
    initials: 'C',
    bgColor: 'bg-purple-500',
    textColor: 'text-white',
  },
  {
    id: 'avatar-4',
    initials: 'D',
    bgColor: 'bg-orange-500',
    textColor: 'text-white',
  },
  {
    id: 'avatar-5',
    initials: 'E',
    bgColor: 'bg-pink-500',
    textColor: 'text-white',
  },
  {
    id: 'avatar-6',
    initials: 'F',
    bgColor: 'bg-indigo-500',
    textColor: 'text-white',
  },
];

// ============================================================================
// AVATAR COMPONENT
// ============================================================================

interface AvatarProps {
  src?: string | null;
  alt?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fallbackInitials?: string;
  className?: string;
}

export function Avatar({
  src,
  alt = 'Avatar',
  size = 'md',
  fallbackInitials,
  className = '',
}: AvatarProps) {
  const sizeClasses = {
    xs: 'h-6 w-6 text-xs',
    sm: 'h-8 w-8 text-sm',
    md: 'h-10 w-10 text-base',
    lg: 'h-12 w-12 text-lg',
    xl: 'h-16 w-16 text-xl',
  };

  const baseClasses = `
    inline-flex items-center justify-center
    rounded-full overflow-hidden
    ${sizeClasses[size]}
    ${className}
  `;

  // If src is provided and not a default avatar ID
  if (src && !src.startsWith('avatar-')) {
    return (
      <div className={baseClasses}>
        <img
          src={src}
          alt={alt}
          className="h-full w-full object-cover"
          onError={(e) => {
            // Fallback if image fails to load
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }}
        />
        <div className="hidden bg-slate-300 text-slate-700 h-full w-full flex items-center justify-center font-medium">
          {fallbackInitials || alt.charAt(0).toUpperCase()}
        </div>
      </div>
    );
  }

  // If src is a default avatar ID
  if (src && src.startsWith('avatar-')) {
    const defaultAvatar = DEFAULT_AVATARS.find((a) => a.id === src);
    if (defaultAvatar) {
      return (
        <div
          className={`${baseClasses} ${defaultAvatar.bgColor} ${defaultAvatar.textColor} font-semibold`}
        >
          {defaultAvatar.initials}
        </div>
      );
    }
  }

  // Fallback to initials
  const initials = fallbackInitials || alt.charAt(0).toUpperCase();
  return (
    <div className={`${baseClasses} bg-slate-300 text-slate-700 font-medium`}>
      {initials}
    </div>
  );
}

// ============================================================================
// USER AVATAR COMPONENT
// ============================================================================

interface UserAvatarProps {
  user?: {
    /**
     * OPTIONAL ON PURPOSE (OFFICEHUB-CLIENT-66). This was typed `string`, and
     * the component trusted it — but it is fed from Firestore-denormalized
     * names and from `auth.displayName`, which is null on invite-only accounts.
     * A missing name threw inside render and the error boundary blanked the
     * surrounding page. Widening the type is part of the fix: the old type made
     * the unguarded `.split` look correct.
     */
    displayName?: string | null;
    photoURL?: string | null;
    email?: string;
  } | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

export function UserAvatar({ user, size = 'md', className = '' }: UserAvatarProps) {
  if (!user) {
    return <Avatar size={size} fallbackInitials="?" className={className} />;
  }

  const initials = getInitials(user.displayName);

  return (
    <Avatar
      src={user.photoURL}
      // `alt` falls back to the component default when there is no name, so the
      // image still has an accessible label.
      alt={user.displayName || undefined}
      fallbackInitials={initials}
      size={size}
      className={className}
    />
  );
}
